/**
* Nom : varietyList.js
* Description : Ce fichier contient la définition de la page varietyList 
*               Cette page liste les variétés avec un filtrage
* Date de création : 23 septembre 2024
*/

/* CSS et assets */
import { AddIcon } from 'package_ui_ihm/dist'
import { DeleteIcon } from 'package_ui_ihm/dist'

/* Bibliothèques et composants */
import { TabRD } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { Item } from 'package_ui_ihm/dist'
import { InputFormSide } from 'package_ui_ihm/dist'
import { ListFilter } from 'package_ui_ihm/dist'
import { SelectFormSide } from 'package_ui_ihm/dist'
import Divider from '@mui/material/Divider';

/* Hook */
import { useState, useEffect } from 'react';
import { useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useScreenWidth } from 'package_ui_ihm/dist'


/**
* VarietyList : affiche la page qui liste les variétés
*                         filtrage de la liste possible selon la catégorie
*                         un lien permet d'afficher un formulaire pour créer une nouvelle catégorie
*                         pour chaque variété une icone permet de la supprimer
*
* Type : page 
*
* Parametres : 
*  None
*/
function VarietyList() {

  // Define useState hooks

  const [varieties, setVarieties] = useState([])
  const [statusVarieties, setStatusVarieties] = useState(null)
  const [isDataLoading, setDataLoading] = useState(false)
  const [isCreation, setCreation] = useState(false)
  const [name, setName] = useState(null);
  const [categoryId, setCategoryId] = useState(0);
  const [nameError, setNameError] = useState(false);
  const [categoryIdError, setCategoryIdError] = useState(false);
  const [isFormClicked, setFormClicked] = useState(false);
  const [selectedCat, setSelectedCat] = useState(0);
  const [categories, setCategories] = useState([])
  const [filteredVarieties, setFilteredVarieties] = useState(varieties);


  // Define useContext hooks
  const { authStatus, authToken, currentUser } = useAuth();
  const { path } = useApi();


  // Define useBlocker hooks
  const shouldBlock = isCreation;
  const blocker = useBlocker(shouldBlock);


  // Define useScreenWidth hooks
  const screenWidth = useScreenWidth();


  // Define functions
  async function retrieveData() {
    // Token
    const token = "Bearer " + authToken
    // Headers
    const headers = {
      accept: 'application/json',
      Authorization: token,
    }
    // Requests in parallel to win time 
    const [resCategory, resVariety] = await Promise.all([
      // Categories
      fetch(path + "/api/bo-cary/", {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }),
      // Varieties
      fetch(path + "/api/bo-vaty/", {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers,
      }),
    ])
    // Responses in parallel to win time
    const [dataCategory, dataVariety] = await Promise.all([
      resCategory.json(),
      resVariety.json(),
    ])
    // Set variables
    setCategories(dataCategory["results"])
    setStatusVarieties(resVariety["status"])
    setVarieties(dataVariety["results"])
    // Dataloading
    setDataLoading(false)
  }

  const handleClick = (event) => {
    event.preventDefault();
    setCreation(true)
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormClicked(true)
    if(name === null || name === ''){
      setNameError(true)
    }
    else
    {   
      setNameError(false)
    }
    if(categoryId === 0 ||categoryId === '0'){
      setCategoryIdError(true)
    }
    else
    {   
      setCategoryIdError(false)
    }
  };

  const handleReset = (event) => {
    event.preventDefault();
    setCreation(false)
    setName(null)
    setNameError(false)
    setCategoryId(0)
    setCategoryIdError(false)
  };

  async function sendData(name, categoryId) {
    // Token
    const token = "Bearer " + authToken
    // Create variety
    await fetch(path + "/api/bo-vaty/", 
      {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        },
        body: JSON.stringify({
            "name": name, 
            "category_id": Number(categoryId)
        })
      }
    );
    setCreation(false)
    setName(null)
    setCategoryId(0)
    retrieveData()
    setFormClicked(false)
  }

  const handleIcon = (event, id) => {
    event.preventDefault();
    sendDelete(id)
  };

  async function sendDelete(id) {
    // Token
    const token = "Bearer " + authToken
    // Delete variety
    await fetch(path + "/api/bo-vaty/" + id + "/", 
      {
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: token,
        }
      }
    );
    retrieveData()
  }

  function updateFilteredVarieties() {
    // Filtering of varieties
    if( selectedCat > 0 )
    {
      setFilteredVarieties(varieties.filter((variety) => variety.category_id === Number(selectedCat)))
    }  
    else
    {
      setFilteredVarieties(varieties)
    }
  }

  const filterReset = (event) => {
    event.preventDefault();
    setFilteredVarieties(varieties)
    setSelectedCat(0)
  }


  // Define useEffect hooks
  useEffect(() => {
    if(authStatus === 200)
    {
      setDataLoading(true)
      retrieveData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(nameError === false && categoryIdError === false && isFormClicked === true){
      sendData(name, categoryId)
    } else {
      setFormClicked(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameError, categoryIdError, isFormClicked])

  useEffect(() => {
    updateFilteredVarieties();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varieties, selectedCat])

  useEffect(() => {
      // Bloquer la fermeture d'onglet/rafraîchissement
      const handleBeforeUnload = (e) => {
      if (shouldBlock === true) {
          // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
          e.preventDefault();
          e.returnValue = "";
          // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
          return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
      }
      }; 

      window.addEventListener("beforeunload", handleBeforeUnload);

      // Nettoyer l'écouteur quand le composant est démonté
      return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock]);

  useEffect(() => {
      if (blocker.state === "blocked") {
          const confirmed = window.confirm(
              "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
          );

          if (confirmed) {
              blocker.proceed();
          } else {
              blocker.reset();
          }
      }
  }, [blocker]);


  // Render component
  return (
    <div>
      <TabRD tabNumber={4}/>
      <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
        <Container>
          {isDataLoading ? (
            <p>Chargement des données</p>
          ) : (
            (statusVarieties === 200) ? (
              <div>
                <h1>Varietés</h1>
                <div className={(screenWidth > 650) ? 'grid-1-3' : null}>
                  {/*New*/}
                  <div className='flex-column'>
                    <div className='link' onClick={handleClick}>
                      <img src={AddIcon} alt='add_item_icon' className='link-img' />
                      <div className='link-text'>Varieté</div>
                    </div>
                    {(isCreation === true) ? (
                      <div className='flex-rows'>
                        <h3>Nouvelle varieté</h3>
                        <form onSubmit={handleSubmit} onReset={handleReset} className='flex-column'>
                          <div className='flex-column form-center'>
                            <InputFormSide error={nameError} type="text" label="Nom" value={name} setValue={setName}/>
                            <SelectFormSide error={categoryIdError} labelText='Categorie' choices={categories} choiceParam={'name'} selectedItem={categoryId} setSelectedItem={setCategoryId}/>
                          </div>
                          <div className='flex-row form-center'>
                            <button type="reset" className='button'>Annuler</button>
                            <button type="submit" className='button'>Valider</button>
                          </div>
                        </form> 
                      </div>
                    ) : (
                      <div></div>
                    )}
                    <h3>Filtres</h3>
                    <ListFilter labelText='Catégorie' choices={categories.sort((a, b) => (a.name > b.name)?1:-1)} choiceParam={'name'} selectedItem={selectedCat} setSelectedItem={setSelectedCat} subfilter={false}/>
                    <div>
                      <button onClick={filterReset} className='button-secondary clickable'>Sans filtres</button>
                    </div>
                  </div>
                  {(screenWidth > 650) ? null : <Divider/>}
                  {/*Varieties list*/}
                  <div className='scroll-list-rd'>
                    <h2 className='subtitle'>Nombre de varietés : {filteredVarieties.length}</h2>
                    {(filteredVarieties && filteredVarieties.length !== 0) ? (
                      filteredVarieties.sort((a, b) => (a.name > b.name)?1:-1).map((variety) => (
                        <Item key={variety['id']} id={variety['id']}>
                          <div className='grid-1-1-2'>
                            <h3 className='item-text'>{variety['name']}</h3> 
                            <p className='item-text'>Catégorie : {(categories.find((category) => category["id"] === variety['category_id']))["name"]}</p>
                            <div className='flex-row justify-end'>
                              <div/>
                              {/*<img src={EditIcon} alt='edit_item_icon' className='link-img link-icon' />*/}
                              { (currentUser["role_name"] === "AT_ADMIN") ? (
                                  <img src={DeleteIcon} alt='delete_item_icon' onClick={(e) => handleIcon(e, variety['id']) } className='link-img'/>
                                ) : (
                                  <p/>
                                )
                              }
                            </div>
                          </div>
                        </Item>
                      ))
                    ) : (
                      <p>Aucune variété actuellement. Veuillez en créer une !</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <h1>Connexion failed</h1>
            )
          )}
        </Container>
      </div>
    </div>
  );
}

export default VarietyList;
